import { Structure } from 'molstar/lib/mol-model/structure';
import {
    Molstar__read_structure_create_chimerax_file__ResidueColorSpec,
    Molstar__read_structure_create_chimerax_file__ResidueSymbolSpec,
    Molstar__read_structure_create_chimerax_file__DiskSpec,
    Molstar__read_structure_create_chimerax_file__ConversionResult,
    Molstar__read_structure_create_chimerax_file__AuthCollision,
} from './molstar__read_structure_create_chimerax_file__Types';
import { molstar__read_structure_create_chimerax_file__ExtractCACoordinates } from './molstar__read_structure_create_chimerax_file__CA-extractor';
import { molstar__read_structure_create_chimerax_file__GenerateColorCommands } from './molstar__read_structure_create_chimerax_file__Color-commands';
import { molstar__read_structure_create_chimerax_file__GenerateSymbolCommands } from './molstar__read_structure_create_chimerax_file__Symbol-commands';
import { molstar__read_structure_create_chimerax_file__GenerateDiskCommands } from './molstar__read_structure_create_chimerax_file__Disk-commands';

export type { Molstar__read_structure_create_chimerax_file__ResidueColorSpec, Molstar__read_structure_create_chimerax_file__ResidueSymbolSpec, Molstar__read_structure_create_chimerax_file__DiskSpec, Molstar__read_structure_create_chimerax_file__ConversionResult };
export type { Molstar__read_structure_create_chimerax_file__SymbolType, Molstar__read_structure_create_chimerax_file__AuthCollision } from './molstar__read_structure_create_chimerax_file__Types';

/**
 * Convert a Molstar Structure with residue annotations into a ChimeraX .cxc
 * command script string.
 *
 * @param structure  A Molstar Structure object (must contain atomic units).
 * @param colorSpecs Per-residue color assignments.
 * @param symbolSpecs Per-residue visual symbols (spheres, arrows, crosses, dots).
 * @param diskSpecs  Cylinders drawn between pairs of residues.
 * @returns A ConversionResult containing the .cxc script and any warnings.
 *
 * @example
 * const result = convertToCXC(structure, colorSpecs, symbolSpecs, diskSpecs);
 * fs.writeFileSync('annotations.cxc', result.script);
 * // In ChimeraX: open annotations.cxc
 */
export function molstar__read_structure_create_chimerax_file__ConvertToCXC(
    {
        structure, colorSpecs, symbolSpecs, diskSpecs
    } : {
        structure: Structure
        colorSpecs: Molstar__read_structure_create_chimerax_file__ResidueColorSpec[]
        symbolSpecs: Molstar__read_structure_create_chimerax_file__ResidueSymbolSpec[]
        diskSpecs: Molstar__read_structure_create_chimerax_file__DiskSpec[]
    }
): Molstar__read_structure_create_chimerax_file__ConversionResult {
    const warnings: string[] = [];
    const collisions: Molstar__read_structure_create_chimerax_file__AuthCollision[] = [];

    // 1. Build CA coordinate map by traversing Molstar Structure
    const { map: caMap, warnings: extractWarnings } = molstar__read_structure_create_chimerax_file__ExtractCACoordinates(structure);
    warnings.push(...extractWarnings);

    // 2. Generate command blocks
    const colorCmds  = molstar__read_structure_create_chimerax_file__GenerateColorCommands(colorSpecs, caMap, warnings, collisions);
    const symbolCmds = molstar__read_structure_create_chimerax_file__GenerateSymbolCommands(symbolSpecs, caMap, warnings);
    const diskCmds   = molstar__read_structure_create_chimerax_file__GenerateDiskCommands(diskSpecs, caMap, warnings);

    // 3. Assemble script
    const sections: string[] = [
        `# ChimeraX command script — generated by molstar-to-chimerax`,
        `# ${new Date().toISOString()}`,
        `# Residues colored: ${colorSpecs.length}  Symbols: ${symbolSpecs.length}  Disks: ${diskSpecs.length}`,
    ];

    if (colorCmds.length > 0) {
        sections.push('', '# --- Residue Coloring ---', ...colorCmds);
    }

    if (symbolCmds.length > 0) {
        sections.push('', '# --- Symbols / Markers ---', ...symbolCmds);
    }

    if (diskCmds.length > 0) {
        sections.push('', '# --- Disks / Cylinders ---', ...diskCmds);
    }

    sections.push('');  // trailing newline

    return {
        script: sections.join('\n'),
        warnings,
        collisions,
    };
}
